import type {CSSProperties} from 'react';
import {WEDGE_DRIVE} from '@territory/tokens';
import {wedgeExactRole as role} from './wedgeRoles.js';

export type WedgeDriveShelfLook = 'drive-shelf-toggle' | 'drive-shelf-row' | 'drive-shelf-route' | 'drive-shelf-route-open' | 'drive-shelf-day-cta'
  | 'drive-shelf-week' | 'drive-shelf-pull' | 'drive-shelf-listen' | 'drive-shelf-listen-playing' | 'drive-shelf-play-day' | 'drive-shelf-zero-primary' | 'drive-shelf-zero-secondary';
const LOOKS: readonly string[] = ['drive-shelf-toggle', 'drive-shelf-row', 'drive-shelf-route', 'drive-shelf-route-open', 'drive-shelf-day-cta',
  'drive-shelf-week', 'drive-shelf-pull', 'drive-shelf-listen', 'drive-shelf-listen-playing', 'drive-shelf-play-day', 'drive-shelf-zero-primary', 'drive-shelf-zero-secondary'];
export const isWedgeDriveShelfLook = (look: string | undefined): look is WedgeDriveShelfLook => look !== undefined && LOOKS.includes(look);

/* Full-width Drive rows keep their released box as the target. An extra region would overlap the next row or the card edge. */
export function wedgeDriveShelfStyle(look: WedgeDriveShelfLook): CSSProperties {
  const centered: CSSProperties = {display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: role('font')};
  if (look === 'drive-shelf-week') return {
    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
    background: 'transparent', border: 0, fontFamily: role('font'),
  };
  if (look === 'drive-shelf-pull') return {
    background: 'none', border: 0, color: role('accent'), fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: role('font'), padding: '2px 4px',
  };
  if (look === 'drive-shelf-listen' || look === 'drive-shelf-listen-playing') {
    const on = look === 'drive-shelf-listen-playing';
    return {
      width: '100%', minHeight: 52, marginTop: 10, ...centered, gap: 9, borderRadius: 14, fontSize: 15, fontWeight: 700,
      border: `1px solid ${role('accent')}`, background: on ? role('accent') : role('accentTint'), color: on ? WEDGE_DRIVE.ink : role('accent'),
    };
  }
  if (look === 'drive-shelf-play-day') return {
    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 16, border: 0, cursor: 'pointer',
    background: role('label'), color: role('bg'), fontFamily: role('font'), boxShadow: WEDGE_DRIVE.playDayShadow,
  };
  if (look === 'drive-shelf-zero-primary') return {
    width: '100%', height: 48, borderRadius: 14, background: role('accent'), color: WEDGE_DRIVE.ink, border: 0, ...centered,
    fontSize: 14.5, fontWeight: 700, gap: 8, boxShadow: `0 6px 18px ${role('accentTint')}`,
  };
  if (look === 'drive-shelf-zero-secondary') return {
    width: '100%', height: 44, borderRadius: 12, background: role('bgElev'), color: role('label'), border: `1px solid ${role('cardBorder')}`,
    ...centered, fontSize: 14, fontWeight: 600, gap: 8,
  };
  if (look === 'drive-shelf-day-cta') return {
    marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '12px 14px', borderRadius: 12, border: 0, cursor: 'pointer',
    background: role('accent'), color: role('onAccent'), fontFamily: role('font'),
    fontSize: 14, fontWeight: 700, textAlign: 'left',
  };
  if (look === 'drive-shelf-route' || look === 'drive-shelf-route-open') return {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '13px 14px', textAlign: 'left', fontFamily: role('font'),
    minHeight: 64, cursor: 'pointer',
    border: `1px solid ${role('accent')}`, background: role('accentTint'),
    borderRadius: look === 'drive-shelf-route-open' ? '16px 16px 0 0' : 16,
  };
  const row = look === 'drive-shelf-row';
  return {
    display: 'flex', alignItems: 'center', gap: row ? 11 : 10, width: '100%',
    padding: row ? '10px 14px' : '12px 14px',
    background: 'none', border: 0, cursor: 'pointer', textAlign: 'left', fontFamily: role('font'),
    ...(row ? {borderTop: `1px solid ${role('cardBorder')}`} : {}),
  };
}
